/**
 * Historial — Activity audit log
 * src/pages/Historial.jsx
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { PlusCircle, Pencil, Trash2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function fmtTs(ts) {
  try {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return '—';
    return `${d.getDate()} ${MONTHS_ES[d.getMonth()]} ${d.getFullYear()} · ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  } catch { return '—'; }
}

// Format details (string or object) as readable multi-line text
function formatDetails(details) {
  if (!details) return null;
  if (typeof details === 'string') return details || null;
  if (typeof details === 'object') {
    const lines = Object.entries(details)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => ({ key: k, val: String(v) }));
    return lines.length ? lines : null;
  }
  return String(details);
}

const ACTION_CONFIG = {
  crear:    { label: 'Creado',    color: '#10b981', bg: 'rgba(16,185,129,0.1)',  Icon: PlusCircle },
  editar:   { label: 'Editado',   color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', Icon: Pencil     },
  eliminar: { label: 'Eliminado', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  Icon: Trash2     },
};
const DEFAULT_CFG = ACTION_CONFIG.editar;
const PERSON_COLOR = (email) => (email || '').includes('alberto') ? '#3b82f6' : '#8b5cf6';

const btnBase = {
  padding: '0.3rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem',
  background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', cursor: 'pointer',
  fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
};
const btnActive = { ...btnBase, background: '#1a472a', color: 'white', border: '1px solid #1a472a' };

export default function Historial({ db }) {
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState('Todos');
  const [person,   setPerson]   = useState('Todos');
  const [expanded, setExpanded] = useState(null); // entry id currently expanded

  const load = useCallback(async () => {
    if (!db) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(collection(db, 'auditLog'), orderBy('timestamp', 'desc'), limit(200))
      );
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Historial:', err);
      setError(err?.message || 'Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => { load(); }, [load]);

  const persons = useMemo(() =>
    [...new Set(entries.map(e => e.userName).filter(Boolean))],
  [entries]);

  const filtered = useMemo(() => entries.filter(e => {
    if (filter !== 'Todos' && e.action !== filter) return false;
    if (person !== 'Todos' && e.userName !== person) return false;
    return true;
  }), [entries, filter, person]);

  const toggle = (id) => setExpanded(prev => prev === id ? null : id);

  return (
    <div className="page-container">
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Historial</h1>
        <button onClick={load} style={{ ...btnBase, display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem' }}>
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {['Todos', 'crear', 'editar', 'eliminar'].map(f => (
          <button key={f} style={filter === f ? btnActive : btnBase} onClick={() => setFilter(f)}>
            {f === 'Todos' ? 'Todos' : (ACTION_CONFIG[f]?.label || f)}
          </button>
        ))}
        {persons.length > 0 && (
          <>
            <div style={{ width: 1, height: 20, background: 'var(--border-color)', margin: '0 0.1rem' }} />
            {['Todos', ...persons].map(p => (
              <button key={p}
                style={person === p ? btnActive : { ...btnBase, color: p === 'Todos' ? 'var(--text-secondary)' : PERSON_COLOR(entries.find(e => e.userName === p)?.userEmail || '') }}
                onClick={() => setPerson(p)}>
                {p}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '0.75rem', marginBottom: '1rem' }}>
          <AlertTriangle size={18} color="#ef4444" />
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ef4444' }}>Error al cargar</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{error}</div>
          </div>
        </div>
      )}

      {/* Count */}
      {!loading && !error && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          {filtered.length} registro{filtered.length !== 1 ? 's' : ''} · Toca una entrada para ver detalles completos
        </div>
      )}

      {loading && <div className="app-loading" style={{ minHeight: '30vh' }}><div className="spinner" /></div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          No hay actividad registrada aún.
          <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Las acciones en la app aparecerán aquí.</div>
        </div>
      )}

      {/* Timeline */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map((entry) => {
            const cfg = ACTION_CONFIG[entry.action] || DEFAULT_CFG;
            const Icon = cfg.Icon;
            const isOpen = expanded === entry.id;
            const details = formatDetails(entry.details);

            return (
              <div key={entry.id}
                onClick={() => toggle(entry.id)}
                style={{ background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: `1px solid ${isOpen ? cfg.color + '40' : 'var(--border-color)'}`, cursor: 'pointer', overflow: 'hidden', transition: 'border-color 0.2s' }}>

                {/* Header row */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.75rem 1rem' }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '0.1rem' }}>
                    <Icon size={15} style={{ color: cfg.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '0.1rem 0.4rem', borderRadius: '0.25rem', flexShrink: 0 }}>
                        {cfg.label}
                      </span>
                      <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {entry.entity || '—'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: PERSON_COLOR(entry.userEmail) }}>
                        {entry.userName || entry.userEmail || '—'}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>
                        {fmtTs(entry.timestamp)}
                      </span>
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, color: 'var(--text-light)', paddingTop: '0.1rem' }}>
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>

                {/* Expanded details panel */}
                {isOpen && details && (
                  <div style={{ borderTop: `1px solid ${cfg.color}30`, background: cfg.bg, padding: '0.75rem 1rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                      Detalles
                    </div>
                    {Array.isArray(details) ? (
                      // Object details: show as key-value table
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                        {details.map(({ key, val }) => (
                          <div key={key} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', minWidth: '5rem', textTransform: 'capitalize', flexShrink: 0 }}>
                              {key}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                              {val}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // String details: show as full text, allow wrapping
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                        {details}
                      </div>
                    )}
                  </div>
                )}

                {/* Collapsed preview of details (if any, no truncation indicator) */}
                {!isOpen && details && (
                  <div style={{ paddingLeft: '3.5rem', paddingRight: '2rem', paddingBottom: '0.6rem', marginTop: '-0.2rem' }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {Array.isArray(details)
                        ? details.map(d => `${d.key}: ${d.val}`).join(' · ')
                        : details}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
